import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";

// MDUI Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";


import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

const StatutoryInfo = () => {
    const [statutoryInfo, setStatutoryInfo] = useState(null);
    const [loading, setLoading] = useState(true);
    const [editMode, setEditMode] = useState(false);
    const [formData, setFormData] = useState({});

    const navigate = useNavigate();

     useEffect(() => {
        const fetchStatutoryInfo = async () => {
            try {
                const userDetails = localStorage.getItem("UserLogDetails");
                if (!userDetails) throw new Error("No user details");

                const decryptedUser = decryptText(userDetails);
                const parsedUser = JSON.parse(decryptedUser);
                const { StudentId, Token } = parsedUser;

                if (!Token) {
                    throw new Error("Missing token");
                }

                const response = await axios.get(
                    API_ENDPOINTS.PROFILE.GET_STATUTORY_INFO(StudentId),
                    {
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${Token}`,
                        },
                    }
                );

                setStatutoryInfo(response.data);
            } catch (error) {
               // console.error("Error fetching statutory info:", error);

                if (error.response?.status === 401 || error.message === "Missing token") {
                    safeToast.error("Session expired. Please log in again.");
                    localStorage.clear();
                    navigate("/BasicAuth");
                } else {
                    safeToast.error("Failed to load statutory info.");
                      safeToast.error("Session expired. Please log in again.");
                    localStorage.clear();
                    navigate("/BasicAuth");
                }
            } finally {
                setLoading(false);
            }
        };

        fetchStatutoryInfo();
    }, [navigate]);

    const handleEdit = () => {
        setFormData({
            txtEx: statutoryInfo.txtEx || "",
            txtPhc: statutoryInfo.txtPhc || "",
            txtidnI: statutoryInfo.txtidnI || "",
            txtidnII: statutoryInfo.txtidnII || "",
        });
        setEditMode(true);
    };

    const handleCancel = () => {
        setEditMode(false);
        setFormData({});
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async () => {
        try {
            const userDetails = localStorage.getItem("UserLogDetails");
            if (!userDetails) throw new Error("User not logged in");

            const decryptedUser = decryptText(userDetails);
            const parsedUser = JSON.parse(decryptedUser);
            const { Token, StudentId, LoginId } = parsedUser;

            // Convert Yes/No to boolean (1/0) for API
            const exServValue = formData.txtEx === "Yes" ? 1 : 0;
            const phyChalValue = formData.txtPhc === "Yes" ? 1 : 0;
            // Use existing boolean value if available, otherwise convert from text
            const fgValue = statutoryInfo.fg !== undefined ? (statutoryInfo.fg ? 1 : 0) : (statutoryInfo.txtfg === "Yes" ? 1 : 0);

            const payload = {
                studentId: parseInt(StudentId),
                community: statutoryInfo.cmid || 0,
                caste: statutoryInfo.csid || 0,
                nation: statutoryInfo.nid || 0,
                orgin: statutoryInfo.orid || 0,
                religion: statutoryInfo.rid || 0,
                fg: fgValue,
                exServ: exServValue,
                txtExtServ: formData.txtEx === "Yes" ? formData.txtEx : null,
                phyChal: phyChalValue,
                txtPhyChal: formData.txtPhc === "Yes" ? formData.txtPhc : null,
                txtVIden1: formData.txtidnI || null,
                txtVIden2: formData.txtidnII || null,
                createdUserId: parseInt(StudentId),
                loginId: LoginId || 0,
            };

            const response = await axios.post(
                API_ENDPOINTS.PROFILE.UPDATE_STATUTORY_INFO,
                payload,
                {
                    headers: {
                        "Content-Type": "application/json",
                        Authorization: `Bearer ${Token}`,
                    },
                }
            );

            if (response.status === 200) {
                safeToast.success("Statutory information updated successfully!");
                // Update local state
                setStatutoryInfo({
                    ...statutoryInfo,
                    txtEx: formData.txtEx,
                    txtPhc: formData.txtPhc,
                    txtidnI: formData.txtidnI,
                    txtidnII: formData.txtidnII,
                });
                setEditMode(false);
            }
        } catch (error) {
            console.error("Error updating statutory info:", error);
            if (error.response?.status === 401) {
                safeToast.error("Session expired. Please log in again.");
                localStorage.clear();
                navigate("/BasicAuth");
            } else {
                safeToast.error("Failed to update statutory information.");
            }
        }
    };

    if (loading) {
        return (
            <MDBox display="flex" justifyContent="center" py={3}>
                <CircularProgress />
            </MDBox>
        );
    }

    if (!statutoryInfo) {
        return (
            <MDBox textAlign="center" py={3}>
                <MDTypography variant="body2" color="error">
                    No statutory information found.
                </MDTypography>
            </MDBox>
        );
    }

    return (
        <>
            <MDBox py={2} px={3}>
                <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
                    {/* Card Header */}
                    <MDBox
                        variant="gradient"
                        bgColor="info"
                        borderRadius="lg"
                        coloredShadow="info"
                        mx={2}
                        mt={-3}
                        p={2}
                        mb={1}
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <MDTypography variant="h6" color="white">
                            {editMode ? "Update Statutory Information" : "Statutory Information"}
                        </MDTypography>
                        {!editMode && (
                            <Tooltip title="Edit Statutory Information">
                                <MDButton variant="outlined" color="light" onClick={handleEdit}>
                                    <Icon>edit</Icon>
                                </MDButton>
                            </Tooltip>
                        )}
                    </MDBox>

                    {/* Card Body */}
                    <CardContent sx={{ backgroundColor: "#fafafa" }}>
                        <Grid container spacing={2}>
                            {[
                                { label: "Community", value: statutoryInfo.cmn, key: "cmn", editable: false },
                                { label: "Caste", value: statutoryInfo.csn, key: "csn", editable: false },
                                { label: "Religion", value: statutoryInfo.rn, key: "rn", editable: false },
                                { label: "Nationality", value: statutoryInfo.nn, key: "nn", editable: false },
                                { label: "State", value: statutoryInfo.orn, key: "orn", editable: false },
                                { label: "First Graduate", value: statutoryInfo.txtfg, key: "txtfg", editable: false },
                                { label: "Ex-Serviceman", value: statutoryInfo.txtEx, key: "txtEx", editable: true },
                                { label: "Physically Challenged", value: statutoryInfo.txtPhc, key: "txtPhc", editable: true },
                                { label: "Identification Mark I", value: statutoryInfo.txtidnI, key: "txtidnI", editable: true },
                                { label: "Identification Mark II", value: statutoryInfo.txtidnII, key: "txtidnII", editable: true },
                            ].map((item, index) => (
                                <Grid item xs={12} sm={6} key={index}>
                                    <MDBox mb={2}>
                                        <InputLabel htmlFor={item.key} sx={{ fontWeight: "bold", mb: 1 }}>
                                            {item.label}
                                        </InputLabel>
                                        {editMode && item.editable ? (
                                            item.key === "txtEx" || item.key === "txtPhc" ? (
                                                <TextField
                                                    id={item.key}
                                                    name={item.key}
                                                    select
                                                    SelectProps={{ native: true }}
                                                    value={formData[item.key] || ""}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                >
                                                    <option value="">Select</option>
                                                    <option value="Yes">Yes</option>
                                                    <option value="No">No</option>
                                                </TextField>
                                            ) : (
                                                <TextField
                                                    id={item.key}
                                                    name={item.key}
                                                    value={formData[item.key] || ""}
                                                    onChange={handleChange}
                                                    fullWidth
                                                    size="small"
                                                    variant="outlined"
                                                    multiline
                                                    rows={2}
                                                />
                                            )
                                        ) : (
                                            <MDTypography variant="body2" fontWeight="regular">
                                                {item.value || "-"}
                                            </MDTypography>
                                        )}
                                    </MDBox>
                                </Grid>
                            ))}
                        </Grid>

                        {editMode && (
                            <MDBox display="flex" justifyContent="flex-end" mt={3}>
                                <MDButton variant="contained" color="info" onClick={handleSave} sx={{ mr: 1 }}>
                                    Save
                                </MDButton>
                                <MDButton variant="outlined" color="secondary" onClick={handleCancel}>
                                    Cancel
                                </MDButton>
                            </MDBox>
                        )}
                    </CardContent>
                </Card>
            </MDBox>
        </>
    );
};

export default StatutoryInfo;
